/**
 * Funnel analytics — lightweight client-side event logger.
 * Writes to public.funnel_events (insert-only, RLS allows anon + authenticated).
 *
 * Captures: device, referrer host + category, UTM params, path, session id.
 * Session id is generated once per browser session and stored in sessionStorage,
 * so anonymous pre-signup events can be stitched to a later trial_started event.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { migrateStorageKey } from "@/lib/migrateStorageKey";
import { hasAnalyticsConsent } from "@/lib/analyticsConsent";

const SESSION_KEY = "myelina_funnel_session_id";
const UTM_KEY = "myelina_funnel_utm";
const LEGACY_SESSION_KEYS = ["elma_funnel_session_id"];
const LEGACY_UTM_KEYS = ["elma_funnel_utm"];

export type FunnelEventName =
  | "landing_view"
  | "signup_started"
  | "trial_started"            // account created (post-verify or first signed-in load)
  | "onboarding_completed"
  | "checkin_completed"
  | "checkout_started"
  | "subscription_created"
  | "subscription_canceled"
  | "access_request_submitted"
  | "access_request_duplicate"
  | "onboarding_next_action"
  // Product-flow events (see src/lib/appAnalytics.ts)
  | "onboarding_started"
  | "onboarding_step_completed"
  | "onboarding_step_back"
  | "prediction_requested"
  | "prediction_created"
  | "prediction_failed"
  | "prediction_viewed"
  | "app_view"
  // Auth recovery + session health (see src/lib/appAnalytics.ts)
  | "password_reset_requested"
  | "password_reset_email_sent"
  | "password_reset_request_failed"
  | "password_reset_link_opened"
  | "password_reset_link_ready"
  | "password_reset_link_invalid"
  | "password_reset_completed"
  | "password_reset_failed"
  | "session_restored"
  | "session_refreshed"
  | "session_expired"
  | "session_signed_out"
  | "session_error"
  // Dashboard load performance (see src/lib/appAnalytics.ts)
  | "dashboard_load_started"
  | "dashboard_load_completed"
  | "dashboard_load_slow"
  | "dashboard_load_failed"
  // Clinician contact (see src/lib/appAnalytics.ts)
  | "clinician_phone_click";

interface UtmParams {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
}

const getSessionId = (): string => {
  try {
    let id = migrateStorageKey(sessionStorage, SESSION_KEY, LEGACY_SESSION_KEYS);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "ssr";
  }
};

const detectDevice = (): "mobile" | "tablet" | "desktop" => {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(ua)) return "tablet";
  if (/mobi|iphone|android.+mobile|phone/.test(ua)) return "mobile";
  // Fallback by viewport width
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

const SEARCH_HOSTS = ["google.", "bing.", "duckduckgo.", "yahoo.", "ecosia.", "brave.", "yandex.", "baidu."];
const SOCIAL_HOSTS = ["facebook.", "fb.", "instagram.", "t.co", "twitter.", "x.com", "linkedin.", "reddit.", "tiktok.", "youtube.", "pinterest.", "threads."];
const EMAIL_HOSTS = ["mail.google.", "outlook.", "mail.yahoo.", "mail.proton."];

const categorizeReferrer = (host: string | null, utm: UtmParams): {
  host: string | null;
  category: "direct" | "search" | "social" | "email" | "referral" | "internal";
} => {
  if (utm.utm_medium) {
    const m = utm.utm_medium.toLowerCase();
    if (["email", "newsletter"].includes(m)) return { host, category: "email" };
    if (["cpc", "ppc", "search", "organic"].includes(m)) return { host, category: "search" };
    if (["social", "social-media", "paid-social"].includes(m)) return { host, category: "social" };
    if (["referral", "affiliate"].includes(m)) return { host, category: "referral" };
  }
  if (!host) return { host: null, category: "direct" };
  const h = host.toLowerCase();
  if (typeof window !== "undefined" && h === window.location.hostname.toLowerCase()) {
    return { host: h, category: "internal" };
  }
  if (SEARCH_HOSTS.some((x) => h.includes(x))) return { host: h, category: "search" };
  if (SOCIAL_HOSTS.some((x) => h.includes(x))) return { host: h, category: "social" };
  if (EMAIL_HOSTS.some((x) => h.includes(x))) return { host: h, category: "email" };
  return { host: h, category: "referral" };
};

/** Capture UTM params from the current URL into sessionStorage once. */
export const captureUtmIfPresent = () => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: UtmParams = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    };
    if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch {
    /* ignore */
  }
};

const getStoredUtm = (): UtmParams => {
  try {
    const raw = migrateStorageKey(sessionStorage, UTM_KEY, LEGACY_UTM_KEYS);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
};

const getReferrerHost = (): string | null => {
  try {
    if (!document.referrer) return null;
    const u = new URL(document.referrer);
    return u.hostname || null;
  } catch {
    return null;
  }
};

/**
 * Log a funnel event. Fire-and-forget — never throws.
 * @param eventName One of the FunnelEventName values.
 * @param metadata  Optional small JSON payload (price_id, invite_code, etc.).
 */
export const trackFunnelEvent = async (
  eventName: FunnelEventName,
  metadata: Record<string, unknown> = {},
): Promise<void> => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  try {
    const utm = getStoredUtm();
    const { host, category } = categorizeReferrer(getReferrerHost(), utm);
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from("funnel_events").insert({
      user_id: user?.id ?? null,
      session_id: getSessionId(),
      event_name: eventName,
      device: detectDevice(),
      referrer_host: host,
      referrer_category: category,
      utm_source: utm.utm_source ?? null,
      utm_medium: utm.utm_medium ?? null,
      utm_campaign: utm.utm_campaign ?? null,
      path: window.location.pathname,
      metadata: metadata as unknown as Json,
    });
  } catch {
    // never break the UI for analytics
  }
};
